<template>
  <el-row :gutter="15">
    <el-col :span="24">
      <el-card shadow="never">
        <el-table
          v-loading="isLoading"
          :data="disruptions"
          stripe
          class="w-100"
        >
          <el-table-column
            label="Name"
            prop="name"
            :formatter="row => row.name || '-'"
          />
          <el-table-column
            label="Start Date"
            prop="start_date"
            :formatter="row => formatDate(row.start_date)"
          />
          <el-table-column
            label="End Date"
            prop="end_date"
            :formatter="row => formatDate(row.end_date)"
          />
          <el-table-column
            label="State"
            prop="state"
            :formatter="row => row.state || '-'"
          />
          <el-table-column
            label="Postcode"
            :formatter="row => formatRange(row.postcode_from, row.postcode_to)"
          />
          <el-table-column
            label="Zone"
            :formatter="row => formatRange(row.zone_from, row.zone_to)"
          />
          <el-table-column
            label="Created"
            prop="created_at"
            :formatter="row => row.created_at ? $moment(row.created_at).fromNow() : '-'"
          />
          <el-table-column
            v-if="canUpdate || canDelete"
            label="Actions"
            align="right"
            width="180"
          >
            <template slot-scope="scope">
              <el-button
                v-if="canUpdate"
                size="mini"
                @click.native="edit(scope.row)"
              >
                Edit
              </el-button>
              <el-button
                v-if="canDelete"
                size="mini"
                type="danger"
                @click.native="remove(scope.row)"
              >
                Delete
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <br>
        <div style="text-align: center;">
          <el-pagination
            :current-page.sync="disruptions.page"
            :page-size.sync="disruptions.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="disruptions.total"
            @size-change="reload"
            @current-change="reload"
          />
        </div>
      </el-card>
    </el-col>

    <el-dialog
      :title="selectedDisruption.id ? 'Edit Service Disruption' : 'Create Service Disruption'"
      width="760px"
      :visible.sync="isDialogVisible"
      :close-on-click-modal="false"
    >
      <el-form
        ref="disruptionForm"
        :model="selectedDisruption"
        :rules="rules"
        label-width="150px"
      >
        <el-form-item
          label="Name"
          prop="name"
        >
          <el-input v-model="selectedDisruption.name" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item
              label="Start Date"
              prop="start_date"
            >
              <el-date-picker
                v-model="selectedDisruption.start_date"
                type="date"
                value-format="yyyy-MM-dd"
                class="w-100"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label="End Date"
              prop="end_date"
            >
              <el-date-picker
                v-model="selectedDisruption.end_date"
                type="date"
                value-format="yyyy-MM-dd"
                class="w-100"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="State">
              <el-select
                v-model="selectedDisruption.state"
                clearable
                class="w-100"
              >
                <el-option
                  v-for="state in states"
                  :key="state"
                  :label="state"
                  :value="state"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Service Type">
              <el-select
                v-model="selectedDisruption.service_type_id"
                clearable
                filterable
                class="w-100"
                placeholder="Select service type"
              >
                <el-option
                  v-for="serviceType in serviceTypes"
                  :key="serviceType.id"
                  :label="serviceType.name"
                  :value="serviceType.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="Postcode From">
              <el-input v-model="selectedDisruption.postcode_from" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Postcode To">
              <el-input v-model="selectedDisruption.postcode_to" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="Zone From">
              <el-input v-model="selectedDisruption.zone_from" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Zone To">
              <el-input v-model="selectedDisruption.zone_to" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="Carrier">
              <el-select
                v-model="selectedDisruption.carrier_id"
                clearable
                filterable
                remote
                :remote-method="remoteMethodCarrier"
                :loading="isLoadingCarriers"
                class="w-100"
                placeholder="Select carrier"
              >
                <el-option
                  v-for="carrier in carriers"
                  :key="carrier.id"
                  :label="carrier.name"
                  :value="carrier.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Carrier Service">
              <el-select
                v-model="selectedDisruption.carrier_service_id"
                clearable
                filterable
                remote
                :remote-method="remoteMethodCarrierService"
                :loading="isLoadingCarrierServices"
                class="w-100"
                placeholder="Select carrier service"
              >
                <el-option
                  v-for="service in carrierServices"
                  :key="service.id"
                  :label="formatCarrierService(service)"
                  :value="service.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Account">
              <el-select
                v-model="selectedDisruption.account_id"
                clearable
                filterable
                remote
                :remote-method="remoteMethodAccount"
                :loading="isLoadingAccounts"
                class="w-100"
                placeholder="Select account"
              >
                <el-option
                  v-for="account in accounts"
                  :key="account.id"
                  :label="account.name"
                  :value="account.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="isDialogVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          :loading="isSaving"
          @click="save"
        >
          Save
        </el-button>
      </template>
    </el-dialog>
  </el-row>
</template>

<script>
import { DELETE, UPDATE } from '@/plugins/user'

export default {
  name: 'ServiceDisruptions',
  data () {
    return {
      title: 'Service Disruptions',
      breadcrumb: {
        name: 'Service Disruptions',
        route: { name: 'configuration-service-disruptions' }
      },
      actions: [
        {
          hidden: false,
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => this.create()
        },
        {
          hidden: false,
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.reload()
        }
      ],
      disruptions: [],
      carriers: [],
      carrierServices: [],
      accounts: [],
      serviceTypes: [],
      isLoadingCarriers: false,
      isLoadingCarrierServices: false,
      isLoadingAccounts: false,
      isLoading: false,
      isSaving: false,
      isDialogVisible: false,
      selectedDisruption: new this.$sdk.servicedisruption(),
      states: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
      rules: {
        name: [
          { required: true, message: 'Name is required', trigger: 'blur' }
        ],
        start_date: [
          { required: true, message: 'Start date is required', trigger: 'change' }
        ],
        end_date: [
          { required: true, message: 'End date is required', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.servicedisruption) || true
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.servicedisruption) || true
    }
  },
  created () {
    this.loadLookups()
    this.load()
  },
  methods: {
    formatCarrierService (service) {
      if (service.carrier && service.carrier.name) {
        return `${service.carrier.name} - ${service.name}`
      }

      return service.name || '-'
    },
    formatDate (value) {
      return value ? this.$moment(value).format('ll') : '-'
    },
    formatRange (from, to) {
      if (from && to) {
        return `${from} - ${to}`
      }

      return from || to || '-'
    },
    create () {
      this.selectedDisruption = new this.$sdk.servicedisruption()
      this.$nextTick(() => {
        if (this.$refs.disruptionForm) {
          this.$refs.disruptionForm.clearValidate()
        }
      })
      this.isDialogVisible = true
    },
    edit (disruption) {
      this.selectedDisruption = disruption.clone()
      this.ensureSelectedLookups()
      this.$nextTick(() => {
        if (this.$refs.disruptionForm) {
          this.$refs.disruptionForm.clearValidate()
        }
      })
      this.isDialogVisible = true
    },
    loadLookups () {
      this.$sdk.commercial_servicetype.list(builder => {
        builder.limit = 500
        builder.order = 'name:asc'
      }).then(res => {
        this.serviceTypes = res
      })
    },
    remoteMethodCarrier (query) {
      const q = (query || '').trim()
      if (!q) {
        this.carriers = []
        return
      }

      this.isLoadingCarriers = true
      this.$sdk.carrier.list(builder => {
        builder.filters = { carrier_name: q }
        builder.order = 'name:asc'
        builder.limit = 25
      })
        .then(res => {
          this.carriers = res
        })
        .finally(() => {
          this.isLoadingCarriers = false
        })
    },
    remoteMethodCarrierService (query) {
      const q = (query || '').trim()
      if (!q) {
        this.carrierServices = []
        return
      }

      this.isLoadingCarrierServices = true
      this.$sdk.search(q, new this.$sdk.carrier_service(), builder => {
        builder.limit = 25
        builder.include = ['carrier']
      })
        .then(res => {
          this.carrierServices = res
        })
        .finally(() => {
          this.isLoadingCarrierServices = false
        })
    },
    remoteMethodAccount (query) {
      const q = (query || '').trim()
      if (!q) {
        this.accounts = []
        return
      }

      this.isLoadingAccounts = true
      this.$sdk.account.list(builder => {
        builder.filters = { account_name: q }
        builder.order = 'name:asc'
        builder.limit = 25
      })
        .then(res => {
          this.accounts = res
        })
        .finally(() => {
          this.isLoadingAccounts = false
        })
    },
    ensureSelectedLookups () {
      if (this.selectedDisruption.carrier_id && !this.carriers.find(carrier => carrier.id === this.selectedDisruption.carrier_id)) {
        this.$sdk.carrier.read(this.selectedDisruption.carrier_id)
          .then(carrier => {
            this.carriers = [carrier, ...this.carriers]
          })
      }

      if (this.selectedDisruption.carrier_service_id && !this.carrierServices.find(service => service.id === this.selectedDisruption.carrier_service_id)) {
        this.$sdk.carrier_service.read(this.selectedDisruption.carrier_service_id, builder => {
          builder.include = ['carrier']
        })
          .then(service => {
            this.carrierServices = [service, ...this.carrierServices]
          })
      }

      if (this.selectedDisruption.account_id && !this.accounts.find(account => account.id === this.selectedDisruption.account_id)) {
        this.$sdk.account.read(this.selectedDisruption.account_id)
          .then(account => {
            this.accounts = [account, ...this.accounts]
          })
      }
    },
    load () {
      this.isLoading = true

      this.$sdk.servicedisruption.list(builder => {
        builder.order = 'created_at:desc'
      })
        .then(res => {
          this.disruptions = res
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async reload () {
      if (!this.disruptions.reload) {
        this.load()
        return
      }

      this.isLoading = true

      try {
        await this.disruptions.reload(builder => {
          builder.order = 'created_at:desc'
        })
      } finally {
        this.isLoading = false
      }
    },
    async save () {
      const isValid = await new Promise(resolve => {
        this.$refs.disruptionForm.validate(valid => resolve(valid))
      })

      if (!isValid) {
        return
      }

      this.isSaving = true

      try {
        await this.selectedDisruption.save()
        this.$message({ message: 'Service disruption saved', type: 'success' })
        this.isDialogVisible = false
        await this.reload()
      } catch (error) {
        this.$message.error('Failed to save service disruption')
      } finally {
        this.isSaving = false
      }
    },
    remove (disruption) {
      this.$confirm('Are you sure you want to delete this service disruption?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(async () => {
          this.isLoading = true

          try {
            await disruption.delete()
            this.$message({ message: 'Service disruption deleted', type: 'success' })
            await this.reload()
          } catch (error) {
            this.$message.error('Failed to delete service disruption')
          } finally {
            this.isLoading = false
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
</style>
